import React, { useState, useEffect, useCallback } from 'react';
import { index } from './algolia';
import './RandomRecord.css';

const RandomRecord = () => {
  const [record, setRecord] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchTotal = async () => {
      try {
        const { nbHits } = await index.search('');
        setTotalRecords(nbHits);
      } catch (err) {
        setError('Failed to connect to database.');
      }
    };
    fetchTotal();
  }, []);

  const fetchRandom = useCallback(async () => {
    if (totalRecords === 0) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const randomPage = Math.floor(Math.random() * totalRecords);
      const { hits } = await index.search('', { hitsPerPage: 1, page: randomPage });
      setRecord(hits[0] || null);
    } catch (err) {
      setError('Failed to fetch a random movie.');
    } finally {
      setLoading(false);
    }
  }, [totalRecords]);

  const handleDelete = async () => {
    if (!record) return;
    if (!window.confirm(`Delete "${record.Title}" from your watchlist?`)) return;
    try {
      await index.deleteObject(record.objectID);
      setRecord(null);
      setTotalRecords((prev) => prev - 1);
      setMessage(`"${record.Title}" removed.`);
    } catch (err) {
      setError('Failed to delete movie.');
    }
  };

  const title = record?.Title || '';
  const year = record?.Year || '';
  const rating = record?.['IMDb Rating'];
  const runtime = record?.['Runtime (mins)'];
  const genres = record?.Genres || '';
  const directors = record?.Directors || '';
  const posterUrl = record?.['Poster Url'];
  const imdbUrl = record?.URL;
  const letterboxdUrl = record?.['Letterboxd URL'];
  const cleanTitle = title.replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
  const justWatchUrl = `https://www.justwatch.com/eg/movie/${encodeURIComponent(cleanTitle)}`;

  return (
    <div className="random-page">
      {/* Poster background */}
      {posterUrl && (
        <div className="random-page__backdrop" key={record?.objectID}>
          <img src={posterUrl} alt="" />
        </div>
      )}

      {/* Content overlay */}
      <div className="random-page__content">
        {!record && (
          <div className="random-page__empty">
            <h1 className="random-page__hero-title">Discover</h1>
            <p className="random-page__subtitle">
              {totalRecords > 0 ? `${totalRecords} movies in your watchlist` : 'Loading...'}
            </p>
            {error && <p className="random-page__error">{error}</p>}
            {message && <p className="random-page__message">{message}</p>}
            <button
              className="random-page__button"
              onClick={fetchRandom}
              disabled={loading || totalRecords === 0}
            >
              {loading ? 'Finding...' : 'Random Movie'}
            </button>
          </div>
        )}

        {record && (
          <div className="random-page__details">
            <h1 className="random-page__title">
              {title} {year && <span className="random-page__year">({year})</span>}
            </h1>

            <div className="random-page__meta">
              {rating && (
                <span className="random-page__rating">
                  &#9733; {parseFloat(rating).toFixed(1)}
                </span>
              )}
              {runtime && <span className="random-page__runtime">{runtime} min</span>}
            </div>

            {genres && <p className="random-page__genres">{genres}</p>}
            {directors && <p className="random-page__directors">Dir: {directors}</p>}

            <div className="random-page__actions">
              {imdbUrl && (
                <a href={imdbUrl} target="_blank" rel="noopener noreferrer" className="btn btn--imdb">
                  IMDb
                </a>
              )}
              {letterboxdUrl && (
                <a href={letterboxdUrl} target="_blank" rel="noopener noreferrer" className="btn btn--letterboxd">
                  Letterboxd
                </a>
              )}
              <a href={justWatchUrl} target="_blank" rel="noopener noreferrer" className="btn btn--watch">
                Watch
              </a>
              <button className="btn btn--delete" onClick={handleDelete}>
                Delete
              </button>
            </div>

            <button
              className="random-page__next"
              onClick={fetchRandom}
              disabled={loading}
            >
              {loading ? 'Finding...' : 'Next Movie'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RandomRecord;
